import { useEffect } from 'react';
import { guestVisitorId, useAuthStore } from '../auth/authStore';
import { heartbeatHall } from '../lobby/api';

/**
 * Site-wide presence: tells the server "this client has the app open" every
 * 25s, whichever page is showing. It is the number behind the lobby's
 * "N playing right now" (see `hallOnlineCount`).
 *
 * Same discipline as the room heartbeat in RoomPage: only while the tab is
 * visible AND the person has interacted in the last few minutes. Otherwise a
 * phone left on the table would count as "playing" forever. When the pings
 * stop, the server's 45s TTL drops the visitor.
 *
 * Runs alongside the room heartbeat rather than replacing it — the server
 * unions the two by visitor key, so a player inside a room is counted once.
 */
export function useHallPresence() {
  const token = useAuthStore((state) => state.token);

  useEffect(() => {
    const visitorId = guestVisitorId();
    const IDLE_MS = 4 * 60 * 1000;
    const ACTIVITY = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    let lastActiveAt = Date.now();
    const markActive = () => { lastActiveAt = Date.now(); };
    const isPresent = () => document.visibilityState === 'visible' && Date.now() - lastActiveAt < IDLE_MS;
    const ping = () => { if (isPresent()) heartbeatHall(token, visitorId).catch(() => undefined); };
    const onVisibility = () => { if (document.visibilityState === 'visible') { lastActiveAt = Date.now(); ping(); } };
    ping();
    const timer = window.setInterval(ping, 25_000);
    const opts: AddEventListenerOptions = { passive: true };
    ACTIVITY.forEach((evt) => window.addEventListener(evt, markActive, opts));
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      ACTIVITY.forEach((evt) => window.removeEventListener(evt, markActive));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token]);
}
